import {
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import { Dialog } from '@/components/AppDialog'
import AddIcon from '@mui/icons-material/Add'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import { useEffect, useState } from 'react'
import { useSaveCancellationPolicy } from '@/api/hooks'
import type { CancellationPolicyView } from '@/api/types'
import { ErrorBanner } from '@/components/common'

type SlabDraft = { daysBeforeEvent: string; chargePercent: string; remarks: string }

const BLANK_SLAB: SlabDraft = { daysBeforeEvent: '', chargePercent: '', remarks: '' }

/** What the house keeps when an event is called off, by the notice given. */
export function CancellationPolicyDialog({
  open,
  policy,
  onClose,
}: {
  open: boolean
  policy?: CancellationPolicyView | null
  onClose: () => void
}) {
  const save = useSaveCancellationPolicy()
  const [code, setCode] = useState('')
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [defaultPolicy, setDefaultPolicy] = useState(false)
  const [slabs, setSlabs] = useState<SlabDraft[]>([{ ...BLANK_SLAB }])

  useEffect(() => {
    if (!open) return
    setCode(policy?.code ?? '')
    setName(policy?.name ?? '')
    setDescription(policy?.description ?? '')
    setDefaultPolicy(policy?.defaultPolicy ?? false)
    setSlabs(
      policy && policy.slabs.length > 0
        ? policy.slabs.map((slab) => ({
            daysBeforeEvent: String(slab.daysBeforeEvent),
            chargePercent: String(slab.chargePercent),
            remarks: slab.remarks ?? '',
          }))
        : [{ ...BLANK_SLAB }],
    )
  }, [open, policy])

  const setSlab = (index: number, patch: Partial<SlabDraft>) =>
    setSlabs((current) => current.map((slab, at) => (at === index ? { ...slab, ...patch } : slab)))

  const filled = slabs.filter((slab) => slab.daysBeforeEvent !== '' && slab.chargePercent !== '')
  const valid = Boolean(code.trim() && name.trim() && filled.length > 0)

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{policy ? `Edit ${policy.name}` : 'New cancellation terms'}</DialogTitle>
      <DialogContent>
        <ErrorBanner error={save.error} />
        <Grid container spacing={2} sx={{ mt: 0 }}>
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              required
              label="Code"
              value={code}
              onChange={(event) => setCode(event.target.value.toUpperCase())}
              disabled={Boolean(policy)}
              helperText={policy ? 'Codes cannot change' : 'Unique'}
            />
          </Grid>
          <Grid item xs={12} sm={5}>
            <TextField
              fullWidth
              required
              label="Name"
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder="Standard cancellation terms"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControlLabel
              control={
                <Switch
                  checked={defaultPolicy}
                  onChange={(event) => setDefaultPolicy(event.target.checked)}
                />
              }
              label="House default"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Description"
              value={description}
              onChange={(event) => setDescription(event.target.value.slice(0, 300))}
              inputProps={{ maxLength: 300 }}
            />
          </Grid>
        </Grid>

        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 3, mb: 1 }}>
          <div>
            <Typography variant="subtitle2">Slabs</Typography>
            <Typography variant="caption" color="text.secondary">
              Cancel within this many days of the event and this much of the order value is charged.
              The tightest slab covering the notice given is the one that applies.
            </Typography>
          </div>
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={() => setSlabs((current) => [...current, { ...BLANK_SLAB }])}
          >
            Add slab
          </Button>
        </Stack>

        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 160 }}>Within days</TableCell>
              <TableCell sx={{ width: 140 }}>Charge %</TableCell>
              <TableCell>Notes</TableCell>
              <TableCell sx={{ width: 48 }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {slabs.map((slab, index) => (
              <TableRow key={index}>
                <TableCell>
                  <TextField
                    size="small"
                    type="number"
                    value={slab.daysBeforeEvent}
                    onChange={(event) => setSlab(index, { daysBeforeEvent: event.target.value })}
                    inputProps={{ 'aria-label': `Days ${index + 1}`, min: 0 }}
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    size="small"
                    type="number"
                    value={slab.chargePercent}
                    onChange={(event) => setSlab(index, { chargePercent: event.target.value })}
                    inputProps={{ 'aria-label': `Charge ${index + 1}`, min: 0, max: 100 }}
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    size="small"
                    fullWidth
                    value={slab.remarks}
                    onChange={(event) => setSlab(index, { remarks: event.target.value.slice(0, 200) })}
                    inputProps={{ 'aria-label': `Notes ${index + 1}`, maxLength: 200 }}
                  />
                </TableCell>
                <TableCell>
                  <IconButton
                    size="small"
                    aria-label={`Remove slab ${index + 1}`}
                    disabled={slabs.length === 1}
                    onClick={() => setSlabs((current) => current.filter((_, at) => at !== index))}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!valid || save.isPending}
          onClick={() =>
            save.mutate(
              {
                id: policy?.id,
                code: code.trim(),
                name: name.trim(),
                description: description || undefined,
                defaultPolicy,
                slabs: filled.map((slab) => ({
                  daysBeforeEvent: Number(slab.daysBeforeEvent),
                  chargePercent: Number(slab.chargePercent),
                  remarks: slab.remarks || undefined,
                })),
              },
              { onSuccess: onClose },
            )
          }
        >
          {policy ? 'Save changes' : 'Create terms'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
